import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import { overviewModels } from "@/mocks/models";
import { overviewDatasets } from "@/mocks/datasets";

export default function Home() {
  const navigate = useNavigate();
  const [models, setModels] = useState(overviewModels);
  const [datasets, setDatasets] = useState(overviewDatasets);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [newModelName, setNewModelName] = useState("");
  const [newDatasetName, setNewDatasetName] = useState("");

  const createModel = () => {
    const name = newModelName.trim();
    if (!name) return;
    const id = `model-${Date.now()}`;
    setModels((prev) => [
      ...prev,
      { id, name, categoryCount: 0, datasetCount: 0, trained: false, lastTrainTime: "" },
    ]);
    setNewModelName("");
    setModelModalOpen(false);
  };

  const createDataset = () => {
    const name = newDatasetName.trim();
    if (!name) return;
    setDatasets((prev) => [...prev, { id: `dataset-${Date.now()}`, name }]);
    setNewDatasetName("");
    setDatasetModalOpen(false);
  };

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground-950">模型总览</h1>
          <p className="mt-1 text-sm text-foreground-500">
            管理文章分类模型与数据源，配置后可进行训练与打标生产。
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setModelModalOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-base" />
            创建模型
          </button>
          <button
            type="button"
            onClick={() => setDatasetModalOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-add-line text-base" />
            添加数据库
          </button>
        </div>
      </div>

      <section className="mt-8 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-50 text-primary-600">
            <i className="ri-node-tree text-base" />
          </span>
          <h2 className="text-base font-semibold text-foreground-950">
            共 {models.length} 个模型
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {models.map((model) => (
            <button
              key={model.id}
              type="button"
              onClick={() => navigate(`/model/${model.id}/settings`)}
              className="group flex cursor-pointer flex-col items-center rounded-lg border border-background-200 bg-background-50 px-4 py-6 transition-all hover:border-primary-300 hover:bg-primary-50/40"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-colors group-hover:bg-primary-100 group-hover:text-primary-700">
                <i className="ri-node-tree text-2xl" />
              </span>
              <span className="mt-3 text-sm font-medium text-foreground-900">{model.name}</span>
              <span
                className={`mt-2 rounded-full px-2 py-0.5 text-xs ${
                  model.trained
                    ? "bg-accent-100 text-accent-800"
                    : "bg-background-200 text-foreground-600"
                }`}
              >
                {model.trained ? "已训练" : "未训练"}
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-50 text-accent-600">
            <i className="ri-database-2-line text-base" />
          </span>
          <h2 className="text-base font-semibold text-foreground-950">
            共 {datasets.length} 个数据库
          </h2>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {datasets.map((dataset) => (
            <button
              key={dataset.id}
              type="button"
              onClick={() => navigate("/datasets")}
              className="group flex cursor-pointer flex-col items-center rounded-lg border border-background-200 bg-background-50 px-4 py-6 transition-all hover:border-accent-300 hover:bg-accent-50/40"
            >
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700 transition-colors group-hover:bg-accent-100 group-hover:text-accent-700">
                <i className="ri-database-2-line text-2xl" />
              </span>
              <span className="mt-3 text-center text-sm font-medium text-foreground-900">
                {dataset.name}
              </span>
            </button>
          ))}
        </div>
      </section>

      <Modal
        open={modelModalOpen}
        title="创建模型"
        onClose={() => setModelModalOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setModelModalOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={createModel}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              确定
            </button>
          </>
        }
      >
        <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="model-name">
          模型名
        </label>
        <input
          id="model-name"
          type="text"
          value={newModelName}
          onChange={(event) => setNewModelName(event.target.value)}
          placeholder="请输入模型名称，例如：城市模型"
          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
        />
      </Modal>

      <Modal
        open={datasetModalOpen}
        title="添加数据库"
        onClose={() => setDatasetModalOpen(false)}
        footer={
          <>
            <button
              type="button"
              onClick={() => setDatasetModalOpen(false)}
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={createDataset}
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              确定
            </button>
          </>
        }
      >
        <label className="mb-1.5 block text-sm font-medium text-foreground-800" htmlFor="dataset-name">
          数据库名称
        </label>
        <input
          id="dataset-name"
          type="text"
          value={newDatasetName}
          onChange={(event) => setNewDatasetName(event.target.value)}
          placeholder="请输入数据库名称"
          className="h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 placeholder:text-foreground-400"
        />
        <p className="mt-3 text-xs text-foreground-500">
          创建后将进入「数据加载」页面配置数据库连接信息。
        </p>
      </Modal>
    </div>
  );
}